import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  azureUploadFile,
  azureDeleteBlob,
  getAzureBlobFile,
  saveContainerWeight,
} from "../../../services/container";
import { getCameraId } from "../../../shared/utils/utils";
import { downloadFile } from "../../../shared/utils/utils";
import { LoadingOverlay } from "../../../components/loader/CharacterizationLoader";
import EmptyState from "../../../components/EmptyState";
import fileIcon from "../../../static/assets/file.svg";
import trashIcon from "../../../static/assets/Trash.svg";
const CharacterizationFiles = ({
  files = [],
  setFiles,
  container,
  period,
  superContainer,
  client,
  historyMaterial = [],
  selectedFilesForExport = [],
  setSelectedFilesForExport,
}) => {
  const { t } = useTranslation();
  const azureUrl = process.env.REACT_APP_AZURE_URL;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const getContainerId = () => {
    if (
      superContainer &&
      superContainer.containerObjectIdList?.includes(container._id)
    ) {
      return superContainer._id;
    }
    return container._id;
  };
  const getImageUrlFromHistoryItem = (h) =>
    h?.image_url ||
    (Array.isArray(h?.image_urls) && h.image_urls.length > 0
      ? h.image_urls[0]
      : null);

  const getCameraIdFromPeriod = () => {
    const imageUrl =
      period?.image_url ||
      (Array.isArray(historyMaterial) && historyMaterial.length > 0
        ? historyMaterial.map(getImageUrlFromHistoryItem).find(Boolean)
        : null) ||
      container?.image_url ||
      "";
    const cameraId = getCameraId(imageUrl);
    return cameraId || null;
  };
  const getBlobName = (cameraId, containerId, fileName) => {
    if (!period?._time && !period?.start) {
      throw new Error("Period time is required for file upload");
    }
    const timestamp = (period._time || period.start)
      .replace(/:/g, "_")
      .slice(0, -1);
    return `camera-ak_${cameraId}/files/${containerId}/${timestamp}/${fileName}`;
  };
  const updateOutputClient = async (newFiles, containerId, cameraId) => {
    try {
      if (!period?._time && !period?.start) {
        throw new Error("Period time is required");
      }
      const timestamp = (period._time || period.start)
        .replace(/:/g, "_")
        .slice(0, -1);
      const outputClientBlob = `camera-ak_${cameraId}/output-client/${timestamp}-camera-ak_${cameraId}-${containerId}-client.json`;
      const clientData = client || {};

      // Ensure waste_category is a JSON string
      let wasteCategoryString = "";
      if (period?.wasteCategoryName) {
        try {
          // If it's already a JSON string, parse and re-stringify to ensure validity
          const parsed =
            typeof period.wasteCategoryName === "string"
              ? JSON.parse(period.wasteCategoryName)
              : period.wasteCategoryName;
          wasteCategoryString = JSON.stringify(parsed);
        } catch {
          // If it's not JSON, treat it as a plain string and wrap it
          const categoryName =
            typeof period.wasteCategoryName === "string"
              ? period.wasteCategoryName
              : period.wasteCategoryName?.wasteCategoryName || "";
          if (categoryName) {
            wasteCategoryString = JSON.stringify({
              wasteCategoryName: categoryName,
            });
          }
        }
      }

      const newOutputClient = {
        id: containerId,
        time: period._time || period.start,
        weight: period?.weight
          ? parseFloat(String(period.weight).replace(",", ".")) / 1000
          : null,
        blobName: outputClientBlob,
        waste_category: wasteCategoryString,
        comments: period?.comments ? JSON.stringify(period.comments) : "[]",
        toCharacterization: false,
        client: JSON.stringify({
          name: clientData.name || "",
          code: clientData.code || "",
          address: clientData.address || "",
          checkStatus: clientData.checkStatus || "-",
          tags: clientData.tags || [],
          files: newFiles,
        }),
      };
      await saveContainerWeight(newOutputClient);
      setFiles(newFiles);
    } catch (error) {
      throw error; // Re-throw to be caught by the caller
    }
  };
  const handleImport = () => {
    fileInputRef.current?.click();
  };
  const onFileChange = async (event) => {
    setError(null);
    const file = event.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      setError(t("Only PDF files are allowed"));
      toast.error(t("Only PDF files are allowed"));
      return;
    }
    setIsLoading(true);
    try {
      const containerId = getContainerId();
      const cameraId = getCameraIdFromPeriod();
      if (!cameraId) {
        throw new Error("Camera ID not found. Cannot upload file.");
      }
      const blobName = getBlobName(cameraId, containerId, file.name);
      const reader = new FileReader();

      reader.onerror = () => {
        setError(t("Failed to read file"));
        toast.error(t("Failed to read file"));
        setIsLoading(false);
      };

      reader.onloadend = async () => {
        try {
          if (!reader.result) {
            throw new Error("FileReader result is empty");
          }
          const base64File = reader.result.split(",")[1];
          if (!base64File) {
            throw new Error("Failed to extract base64 data from file");
          }
          const response = await azureUploadFile({ base64File, blobName });

          if (response.status === 200 || response.status === 201) {
            const newFiles = Array.from(new Set([...files, blobName]));
            // Mettre à jour immédiatement la liste des fichiers pour affichage
            setFiles(newFiles);
            try {
              await updateOutputClient(newFiles, containerId, cameraId);
              toast.success(t("File uploaded successfully"));
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
            } catch (updateError) {
              // File was uploaded but failed to update metadata
              // Keep the file in the list since upload was successful
              toast.warning(t("File uploaded but failed to save metadata"));
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
            }
          } else {
            setError(t("Failed to upload file"));
            toast.error(t("Failed to upload file"));
          }
        } catch (uploadError) {
          if (uploadError.response?.status === 413) {
            const errorMsg =
              uploadError.response.data?.message ||
              t("File is too large (max 10MB)");
            setError(errorMsg);
            toast.error(errorMsg);
          } else {
            const errorMessage =
              uploadError.message || t("Failed to upload file");
            setError(errorMessage);
            toast.error(errorMessage);
          }
        } finally {
          setIsLoading(false);
        }
      };

      reader.readAsDataURL(file);
    } catch (err) {
      setError(err.message);
      toast.error(err.message || t("Failed to upload file"));
      setIsLoading(false);
    }
  };
  const handleDownload = async (file) => {
    if (!file) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await getAzureBlobFile({
        blobUrl: `${azureUrl}${file}`,
      });
      const fileName = file.split("/").pop();
      downloadFile(response.data, fileName);
    } catch (error) {
      toast.error(t("Failed to download file"));
    } finally {
      setIsLoading(false);
    }
  };
  const handleDelete = async (file) => {
    if (!file) return;
    if (!window.confirm(t("Are you sure you want to delete this file?"))) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const containerId = getContainerId();
      const cameraId = getCameraIdFromPeriod();
      if (!cameraId) {
        throw new Error("Camera ID not found. Cannot delete file.");
      }
      await azureDeleteBlob({ blobName: file });
      const newFiles = files.filter((item) => item !== file);
      await updateOutputClient(newFiles, containerId, cameraId);
      toast.success(t("File deleted successfully"));
    } catch (error) {
      toast.error(t("Failed to delete file"));
    } finally {
      setIsLoading(false);
    }
  };
  const getFileName = (file) => {
    if (typeof file === "string") {
      const parts = file.split("/");
      return parts[parts.length - 1] || t("File");
    }
    return file?.name || file?.filename || t("File");
  };
  return (
    <div className="w-full">
      {isLoading && <LoadingOverlay />}
      {error && (
        <div className="mb-4 p-4 bg-[#fbebed] border border-[#f3c6cd] rounded-lg text-[#b9384f]">
          {error}
        </div>
      )}
      {files.length === 0 ? (
        <div className="table-wrapper table-wrapper--cols-3">
          <EmptyState
            title={t("No files yet")}
            description={t(
              "You can add documents related to the characterization",
            )}
            buttonText={t("Import")}
            onButtonClick={handleImport}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={onFileChange}
            style={{ display: "none" }}
          />
        </div>
      ) : (
        <div className="table-wrapper table-wrapper--cols-3 bg-white rounded-lg border border-[#EAEAEA] p-6 min-h-[100px]">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-[20px] font-bold text-[#333333]">
              {t("Imported documents")}
            </h2>
            <button
              type="button"
              onClick={handleImport}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-[#2AB9B8] hover:opacity-90 text-white font-medium rounded-full transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-[14px]"
            >
              <i className="fas fa-download" />
              <span>{t("Import")}</span>
              <i className="fas fa-chevron-down text-xs" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={onFileChange}
              style={{ display: "none" }}
            />
          </div>
          <div className="space-y-3">
            {files.map((file, index) => {
              const fileName = getFileName(file);
              const fileKey =
                typeof file === "string" ? file : file._id || file.id || index;
              return (
                <div
                  key={fileKey}
                  className="flex items-center justify-between p-4 bg-white border border-[#EAEAEA] rounded-lg"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <img
                      src={fileIcon}
                      alt=""
                      className="w-6 h-6 flex-shrink-0"
                    />
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => handleDownload(file)}
                        disabled={isLoading}
                        className="text-[14px] text-[#333333] font-bold truncate text-left underline hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {fileName}
                      </button>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#E0F7F6] text-[#2AB9B8] rounded-full text-[14px] font-semibold flex-shrink-0">
                        <i className="fas fa-check text-xs" />
                        {t("Imported")}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleDownload(file)}
                      disabled={isLoading}
                      className="px-4 py-2 bg-white border border-[#EAEAEA] text-[#333333] hover:bg-[#f8f8f8] font-medium text-[14px] transition-colors rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("Download")}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(file)}
                      disabled={isLoading}
                      className="w-10 h-10 flex items-center justify-center border border-[#EAEAEA] bg-white rounded-full text-[#333333] hover:bg-[#f8f8f8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label={t("Delete")}
                    >
                      <img src={trashIcon} alt="" className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
export default CharacterizationFiles;
